import {
	ColumnModeTypeEnum,
	createColumnEditor,
	SelectCellRenderer,
	SuperGridColDef,
} from '@aleph/supergrid';
import {
	createValueSetter,
	DATE_TIME_ISO_FORMAT,
	DateCellRenderer,
	DefaultCellRenderer,
	TextEditor,
	SelectCellEditor,
	NameHeaderRenderer,
} from '@aleph/shared-components';
import { Media } from 'wb-reddit/graphql/generated';
import {
	AdSuperGridListQueryItems,
	MediaType,
	AdThirdPartyTrackingMeasurementPartner,
	GroupAssetFormat,
	AdCallToActionLanguage,
	AdCallToActionKey,
} from 'wb-spotify/graphql/generated';
import SpotifyMediaCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/media';
import SpotifyMediaCellEditor from 'wb-spotify/app/routes/superGrid/columnEditors/media/cell';
import SpotifyMediaColumnEditor from 'wb-spotify/app/routes/superGrid/columnEditors/media/column';
import { capitalize } from 'lodash';
import { adHeaderNames } from 'wb-spotify/app/routes/superGrid/columnDefinitions/headerNames';
import DateTimeCellEditor from 'wb-reddit/app/routes/superGrid/cellEditors/datetime/datetimeCellEditor';
import dayjs from 'dayjs';
import { editableOnThisLevel } from 'wb-spotify/app/routes/superGrid/helpers';
import AdPreviewCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/adPreview';

export const AdCallToActionKeyOptions = Object.values(AdCallToActionKey).map((key) => ({
	label: capitalize(key.toLowerCase().replace(/_/g, ' ')),
	value: key,
}));

export const LanguageOptions = Object.values(AdCallToActionLanguage).map((lang) => ({
	label: capitalize(lang.toLowerCase()),
	value: lang,
}));

export const MeasurementOptions = Object.values(AdThirdPartyTrackingMeasurementPartner).map((partner) => ({
	label: partner,
	value: partner,
}));

export type AdMedia = Partial<Media> | undefined | null;

// TODO describe params
export const canEditMedia = (
	params: any,
	field: 'adAsset' | 'adCompanionAsset' | 'adLogoAsset' | 'adCanvasAsset',
): boolean => {
	switch (params?.data?.parent?.groupAssetFormat) {
		case GroupAssetFormat.Audio:
			return ['adAsset', 'adCompanionAsset', 'adLogoAsset', 'adCanvasAsset'].includes(field);
		case GroupAssetFormat.Image:
		case GroupAssetFormat.Video:
			return ['adAsset', 'adCompanionAsset', 'adLogoAsset'].includes(field);
		default:
			return false;
	}
};

const AD_COLUMN_DEFINITIONS: SuperGridColDef<AdSuperGridListQueryItems>[] = [
	{
		columnMode:      ColumnModeTypeEnum.VERTICAL,
		headerComponent: NameHeaderRenderer,
		cellRenderer:    AdPreviewCellRenderer,
		colId:           'preview',
		headerName:      'Preview',
	},
	{
		columnMode:   ColumnModeTypeEnum.REGULAR,
		field:        'id',
		elasticChild: 'settingsElastic',
		cellRenderer: DefaultCellRenderer,
		hide:         true,
	},
	{
		field:        'adAdvertiserName',
		cellRenderer: DefaultCellRenderer,
		width:        400,
		headerName:   adHeaderNames.adAdvertiserName,
		elasticChild: 'settingsElastic',
		editable:     editableOnThisLevel,
		cellEditor:   TextEditor,
		valueSetter:  createValueSetter('adAdvertiserName'),
		columnMode:   ColumnModeTypeEnum.BOTH,
	},
	{
		field:        'adTagline',
		cellRenderer: DefaultCellRenderer,
		headerName:   adHeaderNames.adTagline,
		elasticChild: 'settingsElastic',
		editable:     editableOnThisLevel,
		cellEditor:   TextEditor,
		valueSetter:  createValueSetter('adTagline'),
		columnMode:   ColumnModeTypeEnum.BOTH,
	},
	{
		field:            'adCallToActionLanguage',
		cellRenderer:     SelectCellRenderer,
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: LanguageOptions,
		}),
		cellRendererParams: (params) => ({
			options:  LanguageOptions,
			disabled: params.data.isCreate,
		}),
		headerName:   adHeaderNames.adCallToActionLanguage,
		elasticChild: 'settingsElastic',
		editable:     editableOnThisLevel,
		columnMode:   ColumnModeTypeEnum.BOTH,
		valueSetter:  createValueSetter('adCallToActionLanguage'),
	},
	{
		field:            'adCallToActionKey',
		cellRenderer:     SelectCellRenderer,
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: AdCallToActionKeyOptions,
		}),
		cellRendererParams: (params) => ({
			options:  AdCallToActionKeyOptions,
			disabled: params.data.isCreate,
		}),
		headerName:   adHeaderNames.adCallToActionKey,
		elasticChild: 'settingsElastic',
		editable:     editableOnThisLevel,
		valueSetter:  createValueSetter('adCallToActionKey'),
		columnMode:   ColumnModeTypeEnum.BOTH,
	},
	{
		field:        'adCallToActionClickthroughUrl',
		cellRenderer: DefaultCellRenderer,
		headerName:   adHeaderNames.adCallToActionClickthroughUrl,
		elasticChild: 'settingsElastic',
		editable:     editableOnThisLevel,
		cellEditor:   TextEditor,
		valueSetter:  createValueSetter('adCallToActionClickthroughUrl'),
		columnMode:   ColumnModeTypeEnum.BOTH,
	},
	{
		field:              'adAsset',
		colId:              'adAsset',
		width:              285,
		cellRenderer:       SpotifyMediaCellRenderer,
		cellRendererParams: (params) => ({
			mediaTypes: [MediaType.Audio, MediaType.Video, MediaType.Image],
			disabled:   params.data.isCreate,
		}),
		headerName:   adHeaderNames.adAsset,
		elasticChild: 'settingsElastic',
		columnMode:   ColumnModeTypeEnum.BOTH,
		editable:     (params) => editableOnThisLevel(params) && canEditMedia(params, 'adAsset'),
		...createColumnEditor({
			cellEditor:   SpotifyMediaCellEditor,
			columnEditor: (params) => (
				<SpotifyMediaColumnEditor
					{...params}
					mediaTypes={[MediaType.Audio, MediaType.Video, MediaType.Image]}
					type="asset"
				/>
			),
		}),
		valueSetter: createValueSetter('adAsset'),
	},
	{
		field:              'adCompanionAsset',
		colId:              'adCompanionAsset',
		width:              285,
		cellRenderer:       SpotifyMediaCellRenderer,
		headerName:         adHeaderNames.adCompanionAsset,
		elasticChild:       'settingsElastic',
		columnMode:         ColumnModeTypeEnum.BOTH,
		cellRendererParams: (params) => ({
			mediaTypes: [MediaType.Image],
			disabled:   params.data.isCreate,
		}),
		editable: (params) => editableOnThisLevel(params) && canEditMedia(params, 'adCompanionAsset'),
		...createColumnEditor({
			cellEditor:   SpotifyMediaCellEditor,
			columnEditor: (params) => (
				<SpotifyMediaColumnEditor {...params}
					type="companion"
					mediaTypes={[MediaType.Image]}
				/>
			),
		}),
		valueSetter: createValueSetter('adCompanionAsset'),
	},
	{
		field:              'adLogoAsset',
		colId:              'adLogoAsset',
		width:              285,
		cellRenderer:       SpotifyMediaCellRenderer,
		headerName:         adHeaderNames.adLogoAsset,
		cellRendererParams: (params) => ({
			mediaTypes: [MediaType.Image],
			disabled:   params.data.isCreate,
		}),
		elasticChild: 'settingsElastic',
		columnMode:   ColumnModeTypeEnum.BOTH,
		editable:     (params) => editableOnThisLevel(params) && canEditMedia(params, 'adLogoAsset'),
		...createColumnEditor({
			cellEditor:   SpotifyMediaCellEditor,
			columnEditor: (params) => (<SpotifyMediaColumnEditor {...params}
				type="logo"
				mediaTypes={[MediaType.Image]}
			/>),
		}),
		valueSetter: createValueSetter('adLogoAsset'),
	},
	{
		field:              'adCanvasAsset',
		colId:              'adCanvasAsset',
		width:              285,
		cellRenderer:       SpotifyMediaCellRenderer,
		cellRendererParams: (params) => ({
			mediaTypes: [MediaType.Video, MediaType.Image],
			disabled:   params.data.isCreate,
		}),
		headerName:   adHeaderNames.adCanvasAsset,
		elasticChild: 'settingsElastic',
		columnMode:   ColumnModeTypeEnum.BOTH,
		editable:     (params) => editableOnThisLevel(params) && canEditMedia(params, 'adCanvasAsset'),
		...createColumnEditor({
			cellEditor:   SpotifyMediaCellEditor,
			columnEditor: (params) => (
				<SpotifyMediaColumnEditor {...params}
					type="canvas"
					mediaTypes={[MediaType.Image, MediaType.Video]}
				/>
			),
		}),
		valueSetter: createValueSetter('adCanvasAsset'),
	},
	{
		// @ts-ignore
		field:              'adStartTime',
		headerName:         adHeaderNames.adStartTime,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              226,
		editable:           editableOnThisLevel,
		cellEditor:         DateTimeCellEditor,
		cellRenderer:       DateCellRenderer,
		elasticChild:       'settingsElastic',
		cellRendererParams: (params) => ({
			...params,
			icon: true,
		}),
		cellEditorParams: (params) => {
			const groupStartTime = dayjs(params.data.parent.groupStartTime, DATE_TIME_ISO_FORMAT);
			const groupEndTime = params.data.parent.groupEndTime
				? dayjs(params.data.parent.groupEndTime, DATE_TIME_ISO_FORMAT)
				: undefined;
			const now = dayjs();

			// Ad start time must be at least 1 second after group start time
			// Use groupStartTime + 1 second as minDate to allow selecting any time from group start + 1 sec onwards
			let minDate = groupStartTime.isValid() ? groupStartTime.add(1, 'second') : now;

			if (groupEndTime?.isValid() && now.isAfter(groupEndTime)) {
				minDate = groupEndTime;
			}

			// Initial value should be current time if we're past group start + 1 sec, otherwise group start + 1 sec
			const groupStartPlusSecond = groupStartTime.isValid() ? groupStartTime.add(1, 'second') : now;
			const initialValue = now.isAfter(groupStartPlusSecond) && groupStartTime.isValid() ? now : minDate;

			// The validation will still check against the original groupEndTime
			let maxDate = groupEndTime?.isValid() ? groupEndTime : undefined;

			if (maxDate?.isValid()) {
				// Add 1 day to maxDate so that the last day is included when normalized
				// The DatePicker will normalize this to start of day, making the last day selectable
				maxDate = maxDate.add(1, 'day').startOf('day');
			}

			return {
				minDate,
				maxDate,
				initialValue,
			};
		},
	},
	{
		// @ts-ignore
		field:              'adEndTime',
		headerName:         adHeaderNames.adEndTime,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              226,
		// @ts-ignore
		editable:           (params) => editableOnThisLevel(params) && params?.data?.adStartTime,
		cellEditor:         DateTimeCellEditor,
		cellRenderer:       DateCellRenderer,
		elasticChild:       'settingsElastic',
		cellRendererParams: (params) => ({
			...params,
			icon: true,
		}),
		cellEditorParams: (params) => {
			const groupStartTime = dayjs(params.data.parent.groupStartTime, DATE_TIME_ISO_FORMAT);
			const groupEndTime = params.data.parent.groupEndTime
				? dayjs(params.data.parent.groupEndTime, DATE_TIME_ISO_FORMAT)
				: undefined;
			const adStartTime = dayjs(params.data.adStartTime, DATE_TIME_ISO_FORMAT);
			const now = dayjs();

			// minDate should be adStartTime + 1 hour (or groupStartTime if no adStartTime)
			// This allows selecting any time from adStartTime + 1 hour onwards within group boundaries
			const baseMinDate = adStartTime.isValid()
				? adStartTime.add(1, 'hour')
				: groupStartTime.isValid()
					? groupStartTime
					: now;

			// If group has ended, we can't create ads
			let minDate = baseMinDate;

			if (groupEndTime?.isValid() && now.isAfter(groupEndTime)) {
				// Group has ended - set minDate to groupEndTime so picker will show error
				minDate = groupEndTime;
			}

			// The validation will still check against the original groupEndTime
			let maxDate = groupEndTime?.isValid() ? groupEndTime : undefined;

			if (maxDate?.isValid()) {
				// Add 1 day to maxDate so that the last day is included when normalized
				// The DatePicker will normalize this to start of day, making Dec 31 selectable
				maxDate = maxDate.add(1, 'day').startOf('day');
			}

			// Initial value should be the calculated minDate
			const initialValue = minDate;

			return {
				minDate,
				maxDate,
				initialValue,
			};
		},
	},
	{
		// @ts-ignore
		field:            'adThirdPartyTrackingMeasurementPartner',
		columnMode:       ColumnModeTypeEnum.BOTH,
		colId:            'adThirdPartyTrackingMeasurementPartner',
		headerName:       adHeaderNames.adThirdPartyTrackingMeasurementPartner,
		elasticChild:     'settingsElastic',
		cellRenderer:     SelectCellRenderer,
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: MeasurementOptions,
		}),
		cellRendererParams: (params) => ({
			options:  MeasurementOptions,
			disabled: params.data.isCreate,
		}),
		editable:    editableOnThisLevel,
		valueSetter: createValueSetter('adThirdPartyTrackingMeasurementPartner'),
	},
	{
		// @ts-ignore
		field:        'adThirdPartyTrackingUrl',
		columnMode:   ColumnModeTypeEnum.BOTH,
		colId:        'adThirdPartyTrackingUrl',
		headerName:   adHeaderNames.adThirdPartyTrackingUrl,
		elasticChild: 'settingsElastic',
		cellRenderer: DefaultCellRenderer,
		width:        400,
		editable:     editableOnThisLevel,
		cellEditor:   TextEditor,
		valueSetter:  createValueSetter('adThirdPartyTrackingUrl'),
	},
];

export default AD_COLUMN_DEFINITIONS;
